import { Fetch } from '../helpers/fetchWrapper';
import useServiceQuery from '../hooks/useServiceQuery';

const stripeService = {
  _settings: process.env.REACT_APP_USER_API_URL,
  _stripe: `${process.env.REACT_APP_USER_API_URL}/stripe`,

  GetInvoices(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'stripe.invoices',
      params: searchQuery,
      queryFn: () => this.getInvoices(searchQuery),
      refresh: fetch,
    });
    return {
      invoices_loading: loading,
      invoices_error: error,
      invoices_data: data?.data,
      invoices_pagination: data?.pagination,
    };
  },

  async getAllPlans() {
    let res = await Fetch.get(`${this._stripe}/plans`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getStripePlans() {
    let res = await Fetch.get(`${this._stripe}/plans`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async updateSubscription(payload) {
    let res = await Fetch.post(`${this._stripe}/update-subscription-checkout`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  // 409: the plan is already running (a second try after it started), which is just as good.
  async startPlanTrial(payload) {
    let res = await Fetch.post(`${this._stripe}/plans/start-trial`, payload);
    if ((res.status >= 200 && res.status < 300) || res.status === 409) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addExtraLinkedinProfile() {
    let res = await Fetch.post(`${this._stripe}/extra-linkedin-profile`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getStripeConfig() {
    let res = await Fetch.get(`${this._stripe}/config`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async createCheckoutSession(payload) {
    let res = await Fetch.post(`${this._stripe}/create-checkout-session`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async createSetupIntent() {
    let res = await Fetch.post(`${this._stripe}/workspace-admin/setup-intent`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async confirmWorkspaceAdminPayment(payload) {
    let res = await Fetch.post(`${this._stripe}/workspace-admin/confirm-payment`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async startSubscription(payload) {
    let res = await Fetch.post(`${this._stripe}/workspace-admin/start-subscription`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async activatePackage() {
    let res = await Fetch.post(`${this._stripe}/end-trial-now`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async cancelSubscription() {
    let res = await Fetch.post(`${this._stripe}/cancel-subscription`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async cancelMemberSubscription(userId) {
    let res = await Fetch.post(`${this._stripe}/member/cancel-subscription/${userId}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getInvoices({ itemsPerPage = 10, cursor = '' }) {
    let res = await Fetch.get(`${this._stripe}/invoices?itemsPerPage=${itemsPerPage}&cursor=${cursor}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to fetch invoices');
  },
  async updateMemberSubscription(payload) {
    let res = await Fetch.post(`${this._stripe}/member/change-plan`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async saveCardDetails(payload) {
    let res = await Fetch.post(`${this._stripe}/save-card`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  /**
   * Whether AI Home credits can be bought, and on what terms:
   *   { enabled, unavailableReason?, currency, pricePerCredit, min, max }
   */
  GetAiHomeTopUpConfig() {
    const { data, loading } = useServiceQuery({
      name: 'stripe.aiHomeTopUp',
      queryFn: () => this.getAiHomeTopUpConfig(),
      persist: false,
    });
    return { topUp_loading: loading, topUp_config: data };
  },

  async getAiHomeTopUpConfig() {
    const res = await Fetch.get(`${this._stripe}/ai-home-credits`);
    if (res.status >= 200 && res.status < 300) return (await res.json()).data;
    const { message } = await res.json().catch(() => ({}));
    throw new Error(message ?? 'Something went wrong');
  },

  /** A Stripe Checkout for `credits` AI Home credits; resolves to its URL. */
  async createAiHomeTopUpCheckout({ credits, returnTo }) {
    const res = await Fetch.post(`${this._stripe}/ai-home-credits/checkout`, { credits, returnTo });
    if (res.status >= 200 && res.status < 300) return (await res.json()).data.url;
    const { message } = await res.json().catch(() => ({}));
    throw new Error(message ?? 'Could not open the payment page. Please try again.');
  },
};
export default stripeService;
